import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import type { SubscriptionModelData, SubscriptionTier } from "./types";
import { makeObservable, computed } from "mobx";
import { Uuid } from "@/domains/global/identifiers";
import { WithAppStore } from "@/store/types";

export class SubscriptionObservable extends BaseSyncModel<SubscriptionModelData> {
  modelKind = SyncModelKind.Subscription as const;

  constructor({
    id,
    data,
    store,
  }: {
    id: Uuid;
    data: SyncUpdateValue<SubscriptionModelData>;
  } & WithAppStore) {
    super({ id, data, store });
    makeObservable(this, {
      // PROPERTIES
      modelKind: false,
      spaceAccountId: computed,
      provider: computed,
      providerSubscriptionId: computed,
      tier: computed,
      status: computed,
      currentPeriodStart: computed,
      currentPeriodEnd: computed,

      // STATUS HELPERS
      isPaid: computed,
      isFree: computed,
      isActive: computed,
      isPastDue: computed,
      isCanceled: computed,
    });
  }

  get spaceAccountId() {
    return this.modelData.space_account_id;
  }

  get provider() {
    return this.modelData.provider;
  }

  get providerSubscriptionId() {
    return this.modelData.provider_subscription_id;
  }

  get tier(): SubscriptionTier {
    return this.store.platformInfo?.stripeSubscriptionWasCompletedSuccessfully === true
      ? "paid"
      : this.modelData.tier;
  }

  get status() {
    return this.modelData.status;
  }

  get currentPeriodStart() {
    return this.modelData.current_period_start;
  }

  get currentPeriodEnd() {
    return this.modelData.current_period_end;
  }

  get isPaid(): boolean {
    return this.store.platformInfo?.stripeSubscriptionWasCompletedSuccessfully === true
      ? true
      : this.tier === "paid";
  }

  get isFree() {
    return this.tier === "free";
  }

  get isActive() {
    // TODO: https://linear.app/mem-labs/issue/MEM-12621/pass-enums-through-api-so-that-subscriptionsyncmodeldataresponseschema
    return this.status === "active";
  }

  get isPastDue() {
    return this.status === "past_due";
  }

  get isCanceled() {
    return this.status === "canceled";
  }
}
